import { useState, useEffect } from 'react';
import { db, storage } from '../../firebase/config';
import { collection, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Upload } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const AnnouncementForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(isEditMode);
    const [formData, setFormData] = useState({
        title: '',
        date: '',
        content: '',
        image: '', // Cover Image
        detailImage: '' // A4 Content Poster
    });

    // Image Upload State
    const [imageFile, setImageFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');

    // Detail Image Upload State
    const [detailImageFile, setDetailImageFile] = useState(null);
    const [detailPreviewUrl, setDetailPreviewUrl] = useState('');

    useEffect(() => {
        if (isEditMode) {
            const fetchData = async () => {
                const docRef = doc(db, "announcements", id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setFormData(data);
                    if (data.image) setPreviewUrl(data.image);
                    if (data.detailImage) setDetailPreviewUrl(data.detailImage);
                } else {
                    alert("Duyuru bulunamadı!");
                    navigate('/admin/announcements');
                }
                setFetching(false);
            };
            fetchData();
        }
    }, [id, isEditMode, navigate]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleDetailImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setDetailImageFile(file);
            setDetailPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            let imageUrl = formData.image;
            let detailImageUrl = formData.detailImage;

            // Upload Cover Image
            if (imageFile) {
                const storageRef = ref(storage, `announcements/${Date.now()}_${imageFile.name}`);
                const snapshot = await uploadBytes(storageRef, imageFile);
                imageUrl = await getDownloadURL(snapshot.ref);
            }

            // Upload Detail Image
            if (detailImageFile) {
                const storageRef = ref(storage, `announcements/details/${Date.now()}_${detailImageFile.name}`);
                const snapshot = await uploadBytes(storageRef, detailImageFile);
                detailImageUrl = await getDownloadURL(snapshot.ref);
            }

            const dataToSave = {
                title: formData.title,
                date: formData.date,
                content: formData.content,
                image: imageUrl || '',
                detailImage: detailImageUrl || '',
                updatedAt: new Date().toISOString()
            };

            if (isEditMode) {
                await updateDoc(doc(db, "announcements", id), dataToSave);
            } else {
                await addDoc(collection(db, "announcements"), {
                    ...dataToSave,
                    createdAt: new Date().toISOString()
                });
            }

            navigate('/admin/announcements');
        } catch (error) {
            console.error("Error saving announcement:", error);
            alert("Kaydetme sırasında bir hata oluştu: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    if (fetching) return <div className="text-white">Veri yükleniyor...</div>;

    return (
        <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
                <Link to="/admin/announcements" className="p-2 bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors">
                    <ArrowLeft size={20} />
                </Link>
                <h1 className="text-3xl font-bold text-white">
                    {isEditMode ? 'Duyuruyu Düzenle' : 'Yeni Duyuru Ekle'}
                </h1>
            </div>

            <form onSubmit={handleSubmit} className="bg-gray-800 rounded-xl border border-gray-700 p-8 space-y-4">

                {/* Title */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">Başlık</label>
                    <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors"
                        placeholder="Örn: Genel Kurul Çağrısı"
                        required
                    />
                </div>

                {/* Date */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">Tarih</label>
                    <input
                        type="text"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors"
                        placeholder="DD.MM.YYYY"
                        required
                    />
                </div>

                {/* Content */}
                <div>
                    <label className="block text-gray-400 text-sm font-medium mb-2">İçerik</label>
                    <div className="bg-gray-900 border border-gray-700 rounded-lg overflow-hidden">
                        <ReactQuill
                            theme="snow"
                            value={formData.content}
                            onChange={(content) => setFormData({ ...formData, content })}
                            className="text-white h-64"
                            modules={{
                                toolbar: [
                                    [{ 'header': [1, 2, false] }],
                                    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                    ['link'],
                                    ['clean']
                                ],
                            }}
                        />
                    </div>
                    <style>{`
                        .ql-toolbar {
                            background: #e2e8f0;
                            border-bottom: 1px solid #475569 !important;
                        }
                        .ql-container {
                            font-size: 1rem;
                        }
                        .ql-editor {
                            min-height: 16rem;
                            color: #f8fafc; /* Text color inside editor */
                        }
                        .ql-editor.ql-blank::before {
                            color: #94a3b8; /* Placeholder color */
                        }
                    `}</style>
                </div>

                {/* Poster Image */}
                <div className="space-y-4 pt-6 border-t border-gray-700">
                    <h3 className="text-xl font-bold text-white">Duyuru Afişi / Görseli</h3>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-gray-400 text-sm font-medium mb-2">Görsel URL (veya Dosya Yükleyin)</label>
                            <input
                                type="text"
                                value={formData.image || ''}
                                onChange={(e) => {
                                    setFormData({ ...formData, image: e.target.value });
                                    setPreviewUrl(e.target.value);
                                }}
                                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-cyan-500 focus:outline-none transition-colors"
                                placeholder="https://..."
                            />
                        </div>

                        <div className="flex flex-col md:flex-row items-start gap-6">
                            <div className="w-full md:w-64 h-40 bg-gray-900 rounded-lg border-2 border-dashed border-gray-700 flex items-center justify-center overflow-hidden relative group">
                                {previewUrl ? (
                                    <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" onError={(e) => e.target.style.display = 'none'} />
                                ) : (
                                    <div className="text-gray-500 flex flex-col items-center">
                                        <Upload size={24} className="mb-2" />
                                        <span className="text-xs">Afiş Seç (Kapak)</span>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    onChange={handleImageChange}
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                    accept="image/*"
                                />
                            </div>
                            <div className="flex-1 text-sm text-gray-500">
                                <p>Opsiyonel: Liste sayfalarında görünecek küçük kapak görseli.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Detail Poster Image */}
                <div className="space-y-4 pt-6 border-t border-gray-700">
                    <h3 className="text-xl font-bold text-white">Detay Sayfası / A4 Afişi</h3>

                    <div className="space-y-4">
                        <div className="flex flex-col md:flex-row items-start gap-6">
                            <div className="w-full md:w-48 h-64 bg-gray-900 rounded-lg border-2 border-dashed border-gray-700 flex items-center justify-center overflow-hidden relative group">
                                {detailPreviewUrl ? (
                                    <img src={detailPreviewUrl} alt="Detail Preview" className="w-full h-full object-cover" onError={(e) => e.target.style.display = 'none'} />
                                ) : (
                                    <div className="text-gray-500 flex flex-col items-center">
                                        <Upload size={24} className="mb-2" />
                                        <span className="text-xs">A4 Afiş Seç</span>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    onChange={handleDetailImageChange}
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                    accept="image/*"
                                />
                            </div>
                            <div className="flex-1 text-sm text-gray-500">
                                <p>Opsiyonel: Duyurunun detay sayfasına girildiğinde görülecek büyük A4 afiş.</p>
                                {formData.detailImage && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setFormData(prev => ({ ...prev, detailImage: '' }));
                                            setDetailPreviewUrl('');
                                            setDetailImageFile(null);
                                        }}
                                        className="mt-2 text-red-400 text-xs hover:underline"
                                    >
                                        Afişi Kaldır
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Submit */}
                <div className="pt-4 border-t border-gray-700 flex justify-end">
                    <button
                        type="submit"
                        disabled={loading}
                        className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-3 rounded-lg font-bold flex items-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Kaydediliyor...' : (
                            <>
                                <Save size={20} />
                                <span>Kaydet</span>
                            </>
                        )}
                    </button>
                </div>

            </form>
        </div>
    );
};

export default AnnouncementForm;
